import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Drawer,
  Tabs,
  Button,
  Space,
  Typography,
  Tag,
  Tooltip,
  Popover,
  Badge,
  Empty,
} from 'antd';
import {
  CloseOutlined,
  ThunderboltOutlined,
  BulbOutlined,
  RobotOutlined,
  MessageOutlined,
  UserOutlined,
  CrownOutlined,
  PlusOutlined,
  DownOutlined,
  UpOutlined,
  HistoryOutlined,
  CopyOutlined,
  CheckCircleOutlined,
  MinusOutlined,
  SelectOutlined,
} from '@ant-design/icons';
import { message } from 'antd';
import aiReplyService, {
  AIReplyGenerateResponse,
  SubredditGuidelinesResponse,
} from '../../../services/aiReplyService';
import { GenerateParams } from './types';
import CommunityGuidelinesPanel from './CommunityGuidelinesPanel';
import QuickReplyForm, { QuickReplyFormHandle } from './QuickReplyForm';
import AdvancedReplyForm, { AdvancedReplyFormHandle } from './AdvancedReplyForm';
import ConversationView, { AiMessage } from './ConversationView';
import {
  useReplyComposer,
  HistoryEntry,
} from '../../../contexts/ReplyComposerContext';

const { Text, Paragraph } = Typography;

// ---------------------------------------------------------------------------
// Design tokens
// ---------------------------------------------------------------------------

const T = {
  brand: '#FF4500',
  brandHover: '#E03D00',
  bg: '#FFFFFF',
  bgMuted: '#F8F9FA',
  bgSubtle: '#F6F7F8',
  border: '#E5E7EB',
  borderStrong: '#D5D7DB',
  text: '#0F0F10',
  textSecondary: '#5C5C66',
  textMuted: '#898A8F',
  accent: '#0079D3',
};

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

const ReplyComposerDrawer: React.FC = () => {
  const composer = useReplyComposer();
  const {
    activeSession,
    minimizeActive,
    closeSession,
    updateSession,
    startGeneration,
    callOnReplySaved,
    callOnGoToPost,
  } = composer;

  // Local UI state that doesn't need to survive session switches
  const [canGenerate, setCanGenerate] = useState(true);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [guidelines, setGuidelines] = useState<SubredditGuidelinesResponse | null>(null);
  const [guidelinesLoading, setGuidelinesLoading] = useState(false);
  const [guidelinesError, setGuidelinesError] = useState<string | null>(null);
  const [guidelinesExpanded, setGuidelinesExpanded] = useState(false);
  const [postContentExpanded, setPostContentExpanded] = useState(false);

  const quickFormRef = useRef<QuickReplyFormHandle>(null);
  const advancedFormRef = useRef<AdvancedReplyFormHandle>(null);

  const session = activeSession;
  const isOpen = !!session;

  // Reset local UI state and fetch guidelines when session changes
  useEffect(() => {
    if (!session) return;
    setCanGenerate(true);
    setHistoryOpen(false);
    setGuidelinesExpanded(false);
    setPostContentExpanded(false);
  }, [session?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  // Fetch subreddit guidelines when session / subreddit changes
  useEffect(() => {
    const subreddit = session?.post.subreddit;
    if (!session || !subreddit) {
      setGuidelines(null);
      setGuidelinesError(null);
      return;
    }
    let cancelled = false;
    setGuidelinesLoading(true);
    setGuidelinesError(null);
    aiReplyService
      .getSubredditGuidelines(subreddit)
      .then((data) => {
        if (!cancelled) setGuidelines(data);
      })
      .catch((err: any) => {
        if (!cancelled) {
          setGuidelines(null);
          setGuidelinesError(err?.message || 'Failed to load subreddit guidelines');
        }
      })
      .finally(() => {
        if (!cancelled) setGuidelinesLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [session?.id, session?.post.subreddit]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleTabChange = (key: string) => {
    if (!session) return;
    updateSession(session.id, { activeTab: key as 'quick' | 'advanced' });
    if (key === 'quick') setCanGenerate(true);
  };

  const handleGenerate = () => {
    if (!session) return;
    if (session.activeTab === 'quick') {
      quickFormRef.current?.submit();
    } else {
      advancedFormRef.current?.generate();
    }
  };

  const handleStartGeneration = (params: GenerateParams) => {
    if (!session) return;
    startGeneration(session.id, session.activeTab, params);
  };

  // Auto-save a reply text and track it in the session
  const autoSaveReply = async (
    replyText: string,
    params: GenerateParams,
    options?: { silent?: boolean }
  ) => {
    if (!session) return;
    try {
      await callOnReplySaved(
        session.id,
        replyText,
        {
          brand_info: params.brand_info,
          tone_preference: params.tone_preference,
          parent_comment_id: params.capturedCommentId,
        },
        { silent: options?.silent ?? false }
      );
      updateSession(session.id, {
        savedReplyTexts: Array.from(new Set([...session.savedReplyTexts, replyText])),
      });
    } catch (err: any) {
      console.error('[ReplyComposerDrawer] auto-save failed:', err);
      message.error(err?.message || 'Failed to auto-save reply');
    }
  };

  const handleRefine = async (instruction: string) => {
    if (!session?.generateParams) return;

    const { generateParams } = session;
    const allAiReplies = session.messages
      .filter((m): m is AiMessage => m.role === 'ai')
      .map((m) => m.reply.final_reply);

    const lastReply = allAiReplies[allAiReplies.length - 1] ?? '';

    const historyLines: string[] = [];
    session.messages.forEach((m) => {
      if (m.role === 'user') {
        historyLines.push(`- user: ${m.text}`);
      } else if (m.role === 'ai' && historyLines.length > 0) {
        historyLines.push(`- assistant: ${(m as AiMessage).reply.final_reply}`);
      }
    });

    // Build a clearly-marked refinement directive. The user's instruction is
    // the HIGHEST-priority signal — it must override any conflicting strategy /
    // tone / brand preferences from the original compose form. We do NOT pass
    // the instruction through `custom_wording`, because the backend treats
    // that field as a literal keyword list (comma-separated phrases that must
    // appear verbatim in the response). Stuffing free-form instructions there
    // (a) made the LLM try to include the instruction text as-is and (b)
    // produced a spurious "keywords not included" warning every time.
    const refinementBlock = [
      '=== REFINEMENT REQUEST (HIGHEST PRIORITY — MUST BE FOLLOWED) ===',
      'This is a REFINEMENT of an existing AI-generated reply, not a fresh generation.',
      'Treat the user instruction below as the top-priority directive and revise the',
      'previous reply accordingly. The instruction itself must NOT appear verbatim in',
      'the output — interpret it and apply it. If it conflicts with any earlier tone,',
      'persona, or brand preference, the refinement instruction wins.',
      '',
      `USER INSTRUCTION: "${instruction}"`,
      '',
      'PREVIOUS REPLY (revise this — keep what works, change what the instruction asks):',
      lastReply,
      '',
      ...(historyLines.length > 0
        ? [
            'REFINEMENT HISTORY (for context — earlier user instructions and assistant outputs):',
            ...historyLines,
            '',
          ]
        : []),
      '=== END REFINEMENT REQUEST ===',
    ].join('\n');

    const combinedContext = [generateParams.additional_context ?? '', refinementBlock]
      .filter(Boolean)
      .join('\n\n');

    const request = {
      ...generateParams,
      post_title: generateParams.post_title ?? '',
      post_content: generateParams.post_content ?? '',
      additional_context: combinedContext,
      // Preserve the user's ORIGINAL custom_wording (keywords from the compose
      // form). Never overwrite it with the refinement instruction.
      custom_wording: generateParams.custom_wording,
    };

    const baseMessages = session.messages;
    updateSession(session.id, {
      messages: [...baseMessages, { role: 'user', text: instruction }],
      refining: true,
    });

    try {
      const result = await aiReplyService.generateReply(request);
      const aiMsg: AiMessage = { role: 'ai', reply: result, isAdvanced: false };

      const newHistory: HistoryEntry = {
        id: `${Date.now()}-${session.history.length}`,
        text: result.final_reply,
        label: 'Refined',
        instruction,
        isAdvanced: false,
        timestamp: Date.now(),
      };

      updateSession(session.id, {
        messages: [...baseMessages, { role: 'user', text: instruction }, aiMsg],
        history: [...session.history, newHistory],
        savedReplyTexts: Array.from(new Set([...session.savedReplyTexts, result.final_reply])),
        refining: false,
      });

      void callOnReplySaved(
        session.id,
        result.final_reply,
        {
          brand_info: generateParams.brand_info,
          tone_preference: generateParams.tone_preference,
          parent_comment_id: generateParams.capturedCommentId,
        },
        { silent: true }
      );
    } catch (err: any) {
      console.error('[ReplyComposerDrawer] refinement failed:', err);
      message.error(err?.message || 'Failed to refine reply');
      updateSession(session.id, {
        messages: baseMessages,
        refining: false,
      });
    }
  };

  const handleAdvancedVariantSelected = (variant: AIReplyGenerateResponse) => {
    if (!session?.generateParams) return;
    // The three variants (Safe / Engaging / Persuasive) are alternative views of
    // a SINGLE advanced generation, not separate refinement versions. Switching
    // between them must NOT add new history entries — that would falsely inflate
    // the version count. Only refinements (chat instructions) and brand-new
    // generations should produce new history versions.
    //
    // We still auto-save each unique variant the user lands on, so they're all
    // available to post later — but we dedupe by text so toggling back and forth
    // between variants doesn't repeatedly hit the save endpoint.
    if (session.savedReplyTexts.includes(variant.final_reply)) return;
    void autoSaveReply(variant.final_reply, session.generateParams, { silent: true });
  };

  const handleNewReply = () => {
    if (!session) return;
    updateSession(session.id, {
      mode: 'compose',
      messages: [],
      generateParams: null,
      savedReplyTexts: [],
      history: [],
      status: 'composing',
      currentStage: 0,
    });
    setHistoryOpen(false);
  };

  const handleClose = () => {
    if (!session) return;
    if (session.status === 'generating') {
      // Minimize instead of closing while generating
      minimizeActive();
    } else {
      closeSession(session.id);
    }
  };

  const handleMaskClick = () => {
    minimizeActive();
  };

  // "Go to Post" — opens the saved-reply post in the inbox detail panel.
  // We minimize the drawer instead of closing it so the user can come back
  // via the minimized rail and keep refining the reply.
  const handleGoToPost = () => {
    if (!session) return;
    callOnGoToPost(session.id);
    minimizeActive();
  };

  const isCommentReply = !!session?.commentTarget;
  const postTitle = session?.post.post_title ?? '';
  const postContent = session?.post.post_selftext ?? '';
  const postUrl = session?.post.post_permalink;
  const subreddit = session?.post.subreddit;
  const initialKeywords = session?.post.matched_keywords ?? [];
  const commentTarget = session?.commentTarget ?? undefined;
  const brandId = session?.post.monitored_subreddit_id; // not ideal but available
  const generating = session?.status === 'generating';
  const mode = session?.mode ?? 'compose';
  const activeTab = session?.activeTab ?? 'quick';

  const formatRelativeTime = (timestamp: number) => {
    const diff = Math.max(0, Date.now() - timestamp);
    const seconds = Math.floor(diff / 1000);
    if (seconds < 5) return 'just now';
    if (seconds < 60) return `${seconds}s ago`;
    const minutes = Math.floor(seconds / 60);
    if (minutes < 60) return `${minutes}m ago`;
    const hours = Math.floor(minutes / 60);
    return `${hours}h ago`;
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
    message.success('Copied to clipboard');
  };

  const history = useMemo(() => session?.history ?? [], [session?.history]);

  const historyContent = useMemo(() => {
    if (history.length === 0) {
      return (
        <div style={{ padding: '8px 4px', width: 320 }}>
          <Empty
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            description={
              <Text style={{ fontSize: 12, color: T.textMuted }}>No previous versions yet</Text>
            }
          />
        </div>
      );
    }

    const reversed = [...history].reverse();
    return (
      <div style={{ width: 360, maxHeight: 440, overflowY: 'auto' }}>
        {reversed.map((entry, idx) => {
          const isCurrent = idx === 0;
          const versionNumber = history.length - idx;
          return (
            <div
              key={entry.id}
              style={{
                padding: '10px 12px',
                borderBottom: idx === reversed.length - 1 ? 'none' : `1px solid ${T.border}`,
                background: isCurrent ? '#FFF7ED' : T.bg,
              }}
            >
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  marginBottom: 6,
                  gap: 8,
                }}
              >
                <Space size={6} wrap={false}>
                  <Text
                    strong
                    style={{ fontSize: 11, color: T.textMuted, textTransform: 'uppercase', letterSpacing: 0.4 }}
                  >
                    v{versionNumber}
                  </Text>
                  <Tag
                    style={{
                      margin: 0,
                      fontSize: 10,
                      padding: '0 6px',
                      lineHeight: '16px',
                      background: isCurrent ? '#FFEDE0' : T.bgSubtle,
                      color: isCurrent ? T.brand : T.textSecondary,
                      border: `1px solid ${isCurrent ? '#FFD4BC' : T.border}`,
                      fontWeight: 600,
                    }}
                  >
                    {entry.label}
                    {entry.variantStyle ? ` · ${entry.variantStyle}` : ''}
                  </Tag>
                  {isCurrent && (
                    <Tag
                      icon={<CheckCircleOutlined />}
                      style={{
                        margin: 0,
                        fontSize: 10,
                        padding: '0 6px',
                        lineHeight: '16px',
                        background: '#ECFDF5',
                        color: '#047857',
                        border: '1px solid #A7F3D0',
                        fontWeight: 600,
                      }}
                    >
                      Current
                    </Tag>
                  )}
                </Space>
                <Text style={{ fontSize: 10, color: T.textMuted, whiteSpace: 'nowrap' }}>
                  {formatRelativeTime(entry.timestamp)}
                </Text>
              </div>
              {entry.instruction && (
                <Paragraph
                  style={{ margin: '0 0 6px 0', fontSize: 11, color: T.textSecondary, fontStyle: 'italic' }}
                  ellipsis={{ rows: 2, expandable: false, tooltip: entry.instruction }}
                >
                  "{entry.instruction}"
                </Paragraph>
              )}
              <Paragraph
                style={{ margin: 0, fontSize: 12, color: T.text, lineHeight: 1.5, whiteSpace: 'pre-wrap' }}
                ellipsis={{ rows: 4, expandable: true, symbol: 'Show more' }}
              >
                {entry.text}
              </Paragraph>
              <div style={{ marginTop: 6 }}>
                <Button
                  size="small"
                  type="text"
                  icon={<CopyOutlined />}
                  onClick={() => copyToClipboard(entry.text)}
                  style={{ fontSize: 11, height: 22, padding: '0 6px', color: T.textSecondary }}
                >
                  Copy
                </Button>
              </div>
            </div>
          );
        })}
      </div>
    );
  }, [history]);

  const drawerTitle = (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, minWidth: 0, flex: 1 }}>
        <div
          style={{
            width: 32,
            height: 32,
            borderRadius: 6,
            background: T.brand,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
          }}
        >
          <RobotOutlined style={{ color: '#fff', fontSize: 15 }} />
        </div>
        <div style={{ minWidth: 0, flex: 1 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <Text strong style={{ fontSize: 14, lineHeight: 1.3, color: T.text }}>
              AI Reply Composer
            </Text>
            {isCommentReply && (
              <Tag
                icon={<MessageOutlined />}
                style={{
                  margin: 0,
                  fontSize: 11,
                  padding: '0 6px',
                  lineHeight: '18px',
                  background: '#EFF6FF',
                  color: T.accent,
                  border: '1px solid #DBEAFE',
                }}
              >
                u/{commentTarget?.comment.author}
              </Tag>
            )}
          </div>
          <Text
            style={{
              fontSize: 12,
              color: T.textMuted,
              display: 'block',
              maxWidth: 420,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              marginTop: 1,
            }}
          >
            {postTitle}
          </Text>
        </div>
      </div>
      <Space size={6}>
        {mode === 'conversation' && history.length > 0 && (
          <Popover
            trigger="click"
            placement="bottomRight"
            open={historyOpen}
            onOpenChange={setHistoryOpen}
            content={historyContent}
            title={
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <HistoryOutlined style={{ color: T.brand }} />
                <Text strong style={{ fontSize: 13, color: T.text }}>
                  Reply history
                </Text>
                <Text style={{ fontSize: 11, color: T.textMuted, marginLeft: 'auto' }}>
                  {history.length} version{history.length === 1 ? '' : 's'}
                </Text>
              </div>
            }
            overlayStyle={{ width: 380 }}
            overlayInnerStyle={{ padding: 0 }}
          >
            <Tooltip title="View all generated versions">
              <Badge
                count={history.length}
                size="small"
                offset={[-2, 2]}
                style={{
                  backgroundColor: T.brand,
                  fontSize: 10,
                  minWidth: 16,
                  height: 16,
                  lineHeight: '16px',
                }}
              >
                <Button
                  size="small"
                  icon={<HistoryOutlined />}
                  onClick={() => setHistoryOpen((v) => !v)}
                  style={{ fontSize: 12, height: 28, borderColor: T.border, color: T.text }}
                >
                  History
                </Button>
              </Badge>
            </Tooltip>
          </Popover>
        )}
        {mode === 'conversation' && (
          <Tooltip title="Open this post in the inbox with the reply attached">
            <Button
              size="small"
              type="primary"
              icon={<SelectOutlined />}
              onClick={handleGoToPost}
              style={{
                fontSize: 12,
                height: 28,
                background: T.brand,
                borderColor: T.brand,
                boxShadow: '0 1px 2px rgba(15, 15, 16, 0.04)',
              }}
            >
              Go to Post
            </Button>
          </Tooltip>
        )}
        {mode === 'conversation' && (
          <Tooltip title="Start a new reply (resets the conversation)">
            <Button
              size="small"
              icon={<PlusOutlined />}
              onClick={handleNewReply}
              style={{ fontSize: 12, height: 28, borderColor: T.border, color: T.text }}
            >
              New Reply
            </Button>
          </Tooltip>
        )}
        {/* Minimize button — always visible */}
        <Tooltip title="Minimize — generation continues in background">
          <Button
            type="text"
            size="small"
            icon={<MinusOutlined />}
            onClick={minimizeActive}
            style={{ color: T.textSecondary }}
          />
        </Tooltip>
        {/* Close button — minimizes while generating, closes when done */}
        <Tooltip
          title={
            generating
              ? 'Reply still generating — click to minimize'
              : 'Close'
          }
        >
          <Button
            type="text"
            size="small"
            icon={<CloseOutlined />}
            onClick={handleClose}
            style={{ color: T.textSecondary }}
          />
        </Tooltip>
      </Space>
    </div>
  );

  return (
    <Drawer
      placement="right"
      width={720}
      open={isOpen}
      onClose={handleMaskClick}
      mask
      maskStyle={{ background: 'transparent' }}
      maskClosable
      closable={false}
      destroyOnClose={false}
      title={drawerTitle}
      styles={{
        header: {
          padding: '12px 16px',
          borderBottom: `1px solid ${T.border}`,
          backgroundColor: T.bg,
        },
        body: {
          padding: 0,
          display: 'flex',
          flexDirection: 'column',
          height: '100%',
          overflow: 'hidden',
          backgroundColor: T.bg,
        },
      }}
    >
      {session && (
        <>
          {/* Sub-header: guidelines + tabs */}
          <div
            style={{
              flexShrink: 0,
              padding: subreddit ? '8px 16px 0' : '4px 16px 0',
              borderBottom: `1px solid ${T.border}`,
              backgroundColor: T.bg,
            }}
          >
            {subreddit && (
              <div style={{ marginBottom: 8 }}>
                <CommunityGuidelinesPanel
                  subreddit={subreddit}
                  guidelines={guidelines}
                  loading={guidelinesLoading}
                  error={guidelinesError}
                  expanded={guidelinesExpanded}
                  onToggle={() => setGuidelinesExpanded((v) => !v)}
                />
              </div>
            )}

            {mode === 'compose' && (
              <Tabs
                activeKey={activeTab}
                onChange={handleTabChange}
                size="small"
                style={{ marginBottom: 0 }}
                tabBarStyle={{ marginBottom: 0, borderBottom: 'none' }}
                items={[
                  {
                    key: 'quick',
                    label: (
                      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                        <ThunderboltOutlined style={{ fontSize: 12 }} />
                        Quick Reply
                      </span>
                    ),
                  },
                  {
                    key: 'advanced',
                    label: (
                      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                        <BulbOutlined style={{ fontSize: 12 }} />
                        Advanced Reply
                      </span>
                    ),
                  },
                ]}
              />
            )}

            {mode === 'conversation' && (
              <div style={{ paddingBottom: 10 }}>
                <Space size={8}>
                  {activeTab === 'quick' ? (
                    <ThunderboltOutlined style={{ fontSize: 12, color: T.brand }} />
                  ) : (
                    <BulbOutlined style={{ fontSize: 12, color: T.brand }} />
                  )}
                  <Text style={{ fontSize: 12, color: T.text, fontWeight: 600 }}>
                    {activeTab === 'quick' ? 'Quick Reply' : 'Advanced Reply'}
                  </Text>
                  <Text style={{ fontSize: 12, color: T.textMuted }}>· Refine using the chat below</Text>
                </Space>
              </div>
            )}
          </div>

          {/* Scrollable body */}
          <div
            style={{
              flex: 1,
              overflowY: mode === 'conversation' ? 'hidden' : 'auto',
              display: 'flex',
              flexDirection: 'column',
              backgroundColor: T.bg,
            }}
          >
            {mode === 'compose' ? (
              <div style={{ flex: 1, overflowY: 'auto', padding: '16px 16px 8px' }}>
                {/* Comment context alert */}
                {isCommentReply && commentTarget && (
                  <div
                    style={{
                      marginBottom: 14,
                      padding: '10px 12px',
                      background: '#EFF6FF',
                      border: `1px solid #DBEAFE`,
                      borderRadius: 8,
                    }}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 6 }}>
                      <MessageOutlined style={{ color: T.accent, fontSize: 12 }} />
                      <Text strong style={{ fontSize: 12, color: T.text }}>Replying to</Text>
                      <UserOutlined style={{ fontSize: 11, color: T.textSecondary }} />
                      <Text style={{ color: T.accent, fontSize: 12, fontWeight: 600 }}>
                        u/{commentTarget.comment.author}
                      </Text>
                      {commentTarget.comment.is_submitter && (
                        <Tag
                          style={{
                            fontSize: 10,
                            padding: '0 4px',
                            lineHeight: '16px',
                            margin: 0,
                            background: '#FEF3C7',
                            color: '#92400E',
                            border: '1px solid #FDE68A',
                          }}
                        >
                          <CrownOutlined style={{ marginRight: 2 }} />OP
                        </Tag>
                      )}
                    </div>
                    <Paragraph
                      style={{
                        margin: 0,
                        fontSize: 12,
                        color: T.textSecondary,
                        background: '#fff',
                        padding: '6px 10px',
                        borderRadius: 6,
                        border: `1px solid ${T.border}`,
                        maxHeight: 72,
                        overflowY: 'auto',
                      }}
                      ellipsis={{ rows: 3, expandable: true }}
                    >
                      "{commentTarget.comment.body}"
                    </Paragraph>
                    {commentTarget.parentChain.length > 0 && (
                      <Text style={{ fontSize: 11, color: T.textMuted, display: 'block', marginTop: 6 }}>
                        Thread depth: {commentTarget.parentChain.length + 1} levels
                      </Text>
                    )}
                  </div>
                )}

                {/* Post context (collapsible) */}
                <div
                  style={{
                    marginBottom: 16,
                    background: T.bgMuted,
                    border: `1px solid ${T.border}`,
                    borderRadius: 8,
                    padding: '10px 12px',
                  }}
                >
                  <div
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      marginBottom: 4,
                    }}
                  >
                    <Text
                      strong
                      style={{
                        fontSize: 11,
                        color: T.textMuted,
                        textTransform: 'uppercase',
                        letterSpacing: 0.4,
                      }}
                    >
                      {isCommentReply ? 'Original Post' : 'Post Title'}
                    </Text>
                    {postContent && !isCommentReply && (
                      <Button
                        type="text"
                        size="small"
                        icon={postContentExpanded ? <UpOutlined /> : <DownOutlined />}
                        style={{ fontSize: 11, padding: '0 6px', height: 22, color: T.textSecondary }}
                        onClick={() => setPostContentExpanded((v) => !v)}
                      >
                        {postContentExpanded ? 'Collapse' : 'Expand'}
                      </Button>
                    )}
                  </div>
                  <Text style={{ fontSize: 13, color: T.text, fontWeight: 500, lineHeight: 1.5 }}>
                    {postTitle}
                  </Text>
                  {postContent && !isCommentReply && postContentExpanded && (
                    <>
                      <div style={{ height: 1, background: T.border, margin: '8px 0' }} />
                      <Text
                        strong
                        style={{
                          fontSize: 11,
                          color: T.textMuted,
                          textTransform: 'uppercase',
                          letterSpacing: 0.4,
                          display: 'block',
                          marginBottom: 4,
                        }}
                      >
                        Post Content
                      </Text>
                      <Paragraph
                        style={{
                          fontSize: 12,
                          margin: 0,
                          maxHeight: 120,
                          overflowY: 'auto',
                          whiteSpace: 'pre-wrap',
                          color: T.textSecondary,
                          lineHeight: 1.55,
                        }}
                      >
                        {postContent}
                      </Paragraph>
                    </>
                  )}
                </div>

                {/* Active form — keyed by session ID so form values reset on session switch */}
                {activeTab === 'quick' ? (
                  <QuickReplyForm
                    key={`quick-${session.id}`}
                    ref={quickFormRef}
                    postTitle={postTitle}
                    postContent={postContent}
                    postUrl={postUrl}
                    subreddit={subreddit}
                    initialKeywords={initialKeywords}
                    commentTarget={commentTarget}
                    guidelines={guidelines}
                    loading={generating}
                    currentStage={session.currentStage}
                    onCanGenerateChange={setCanGenerate}
                    onStartGeneration={handleStartGeneration}
                  />
                ) : (
                  <AdvancedReplyForm
                    key={`advanced-${session.id}`}
                    ref={advancedFormRef}
                    postTitle={postTitle}
                    postContent={postContent}
                    postUrl={postUrl}
                    subreddit={subreddit}
                    brandId={brandId}
                    initialKeywords={initialKeywords}
                    commentTarget={commentTarget}
                    guidelines={guidelines}
                    loading={generating}
                    currentStage={session.currentStage}
                    onCanGenerateChange={setCanGenerate}
                    onStartGeneration={handleStartGeneration}
                  />
                )}
              </div>
            ) : (
              <div
                style={{
                  flex: 1,
                  overflow: 'hidden',
                  display: 'flex',
                  flexDirection: 'column',
                  padding: '16px 16px 0',
                }}
              >
                <ConversationView
                  messages={session.messages}
                  refining={session.refining}
                  onRefine={handleRefine}
                  onVariantSelected={handleAdvancedVariantSelected}
                  savedReplyTexts={new Set(session.savedReplyTexts)}
                  historyCount={history.length}
                />
              </div>
            )}
          </div>

          {/* Sticky footer — only shown in compose mode */}
          {mode === 'compose' && (
            <div
              style={{
                flexShrink: 0,
                padding: '12px 16px',
                borderTop: `1px solid ${T.border}`,
                backgroundColor: T.bg,
              }}
            >
              <Tooltip
                title={
                  !canGenerate
                    ? 'Add a thought in Section 0, or pick at least an Intent, Persona, and Depth.'
                    : undefined
                }
              >
                <Button
                  type="primary"
                  block
                  loading={generating}
                  disabled={!canGenerate}
                  icon={<RobotOutlined />}
                  onClick={handleGenerate}
                  style={
                    canGenerate
                      ? {
                          height: 44,
                          fontSize: 14,
                          fontWeight: 600,
                          background: T.brand,
                          borderColor: T.brand,
                          borderRadius: 8,
                          boxShadow: '0 1px 2px rgba(15, 15, 16, 0.04)',
                        }
                      : { height: 44, fontSize: 14, borderRadius: 8 }
                  }
                >
                  {generating
                    ? 'Generating…'
                    : activeTab === 'quick'
                    ? 'Generate Reply'
                    : 'Generate 3 Variants'}
                </Button>
              </Tooltip>
            </div>
          )}
        </>
      )}
    </Drawer>
  );
};

export default ReplyComposerDrawer;
